import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { PAYROLL_STATUS, deriveStepStatus } from './usePayrollStatus';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_LinkDespesasInput.ts';
import '../contracts/Payroll_ExpenseConciliationCompleteInput';
import '../contracts/Payroll_ExpenseConciliationCompleteResponse';
import '../contracts/Payroll_ExpenseConciliationDespesasResponse';
import '../contracts/Payroll_ExpenseConciliationLinkDespesasResponse';
import '../contracts/Payroll_ExpenseConciliationSummaryResponse';
import '../contracts/Payroll_ExpenseConciliationUnlinkDespesaInput';
import '../contracts/Payroll_ExpenseConciliationUnlinkDespesaResponse';

import type { Payroll_LinkDespesasInput } from '../contracts/Payroll_LinkDespesasInput';
/**
 * Derive UI status from numeric period status and conciliation data
 * Uses centralized deriveStepStatus from usePayrollStatus.js
 *
 * @param {number} periodStatus - The period's main status (0-5, -1)
 * @param {number} count - Number of linked despesas
 * @returns {string} 'pending' | 'in_progress' | 'completed'
 */
const deriveExpenseConciliationStatus = (periodStatus: any, count: any) => {
  const linkedCount = parseInt(count) || 0;
  log('useExpenseConciliation', 'deriveExpenseConciliationStatus', 'Deriving status', {
    periodStatus,
    linkedCount,
    threshold: PAYROLL_STATUS.EXPENSES
  });
  return deriveStepStatus(periodStatus, PAYROLL_STATUS.EXPENSES, linkedCount > 0);
};

/**
 * Composable for Expense Conciliation operations
 * Handles expense conciliation for payroll periods - linking despesas to periods
 */
export function useExpenseConciliation() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Get expense conciliation summary for a payroll period
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Summary with status, amount, count
   */
  const getExpenseConciliationSummary = async (periodId: number) => {
    log('useExpenseConciliation', 'getExpenseConciliationSummary', 'Fetching summary', { periodId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/expense_conciliation_summary/${periodId}`, {
        responseContract: 'Payroll_ExpenseConciliationSummaryResponse'
      });
      log('useExpenseConciliation', 'getExpenseConciliationSummary', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty expense conciliation summary response');
        }
        const data = response.dados;
        const count = data.count;
        const periodStatus = data.period_status;

        return {
          // Derive UI status from numeric period status and count
          status: deriveExpenseConciliationStatus(periodStatus, count),
          amount: data.amount,
          count: count,
          despesas: [] as unknown[]
        };
      } else {
        throw new Error(response.error || 'Failed to fetch expense conciliation summary');
      }
    } catch (err) {
      log('useExpenseConciliation', 'getExpenseConciliationSummary', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Link despesas to payroll period
   * @param {number} periodId - Payroll period ID
   * @param {Array<number>} despesaIds - Array of despesa IDs to link
   * @returns {Promise<Object>} Updated summary
   */
  const linkDespesasToPeriod = async (periodId: any, despesaIds: any) => {
    log('useExpenseConciliation', 'linkDespesasToPeriod', 'Linking despesas', {
      periodId,
      count: despesaIds.length
    });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_LinkDespesasInput = { despesa_ids: despesaIds };
      validate('Payroll_LinkDespesasInput', payload);
      const response = await httpService.post(`/payroll/expense_conciliation_link_despesas/${periodId}`, payload, {
        responseContract: 'Payroll_ExpenseConciliationLinkDespesasResponse'
      });
      log('useExpenseConciliation', 'linkDespesasToPeriod', 'Response', response);

      if (response.success) {
        return response.dados ?? { linked_count: 0 };
      } else {
        throw new Error(response.error || 'Failed to link despesas');
      }
    } catch (err) {
      log('useExpenseConciliation', 'linkDespesasToPeriod', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Unlink a single despesa from payroll period
   * @param {number} periodId - Payroll period ID
   * @param {number} despesaId - Despesa ID to unlink
   * @returns {Promise<boolean>}
   */
  const unlinkDespesaFromPeriod = async (periodId: number, despesaId: number | string) => {
    log('useExpenseConciliation', 'unlinkDespesaFromPeriod', 'Unlinking despesa', {
      periodId,
      despesaId
    });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        `/payroll/expense_conciliation_unlink_despesa/${periodId}/${despesaId}`,
        {},
        {
          inputContract: 'Payroll_ExpenseConciliationUnlinkDespesaInput',
          responseContract: 'Payroll_ExpenseConciliationUnlinkDespesaResponse'
        }
      );
      log('useExpenseConciliation', 'unlinkDespesaFromPeriod', 'Response', response);

      if (response.success) {
        return true;
      } else {
        throw new Error(response.error || 'Failed to unlink despesa');
      }
    } catch (err) {
      log('useExpenseConciliation', 'unlinkDespesaFromPeriod', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Complete expense conciliation - calculates total and updates status
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Final summary with calculated amount
   */
  const completeExpenseConciliation = async (periodId: number) => {
    log('useExpenseConciliation', 'completeExpenseConciliation', 'Completing conciliation', { periodId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        `/payroll/expense_conciliation_complete/${periodId}`,
        {},
        {
          inputContract: 'Payroll_ExpenseConciliationCompleteInput',
          responseContract: 'Payroll_ExpenseConciliationCompleteResponse'
        }
      );
      log('useExpenseConciliation', 'completeExpenseConciliation', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty complete expense conciliation response');
        }
        const data = response.dados;
        return {
          status: 'completed',
          amount: data.amount,
          count: data.count
        };
      } else {
        throw new Error(response.error || 'Failed to complete expense conciliation');
      }
    } catch (err) {
      log('useExpenseConciliation', 'completeExpenseConciliation', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get all despesas linked to a payroll period
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Array>} Array of linked despesas
   */
  const getLinkedDespesas = async (periodId: number) => {
    log('useExpenseConciliation', 'getLinkedDespesas', 'Fetching linked despesas', { periodId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/expense_conciliation_despesas/${periodId}`, {
        responseContract: 'Payroll_ExpenseConciliationDespesasResponse'
      });
      log('useExpenseConciliation', 'getLinkedDespesas', 'Response', response);

      if (response.success) {
        return response.dados ?? [];
      } else {
        throw new Error(response.error || 'Failed to fetch linked despesas');
      }
    } catch (err) {
      log('useExpenseConciliation', 'getLinkedDespesas', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    getExpenseConciliationSummary,
    linkDespesasToPeriod,
    unlinkDespesaFromPeriod,
    completeExpenseConciliation,
    getLinkedDespesas
  };
}
